import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Clock,
  DollarSign,
  Star
} from 'lucide-react';
import Questions from './Questions';
import mentorService from '@services/mentor.service';
import profileService from '@services/profile.service';

const MentorDashboard = () => {
  const navigate = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [mentorProfile, setMentorProfile] = useState(null);
  const [stats, setStats] = useState({
    answered: 0,
    pending: 0,
    earnings: 0,
    rating: 'N/A',
    hasNewEarnings: false
  });

  useEffect(() => {
    fetchDashboardStats();
    fetchMentorProfile();
  }, []);

  const fetchMentorProfile = async () => {
    try {
      const response = await profileService.getMyProfile();
      if (response.success) {
        setMentorProfile(response?.data?.data);
      }
    } catch (error) {
      console.error('Failed to fetch mentor profile:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // Fetch pending questions
      const questionsResponse = await mentorService.getPendingQuestions();
      if (questionsResponse.success) {
        const pending = questionsResponse?.data?.data.total || questionsResponse?.data.data.questions?.length || 0;
        setPendingCount(pending);
        setStats(prev => ({ ...prev, pending }));
      }

      // Fetch earnings
      const earningsResponse = await mentorService.getMentorEarnings();

      if (earningsResponse.success) {
        const avgRating = earningsResponse?.data?.data?.averageRating 
          ? parseFloat(earningsResponse?.data?.data.averageRating).toFixed(1)
          : 'N/A';
        
        const totalEarned = earningsResponse?.data?.data?.paymentInfo?.totalEarned || 0;
        const lastPaymentDate = earningsResponse?.data?.data?.paymentInfo?.lastPaymentDate;
        
        // Check if there's a recent payment (within last 7 days)
        const hasNewEarnings = lastPaymentDate && 
          (new Date() - new Date(lastPaymentDate)) < (7 * 24 * 60 * 60 * 1000);
        
        setStats(prev => ({
          ...prev,
          answered: earningsResponse?.data.data.totalQuestions || 0,
          earnings: totalEarned,
          rating: avgRating,
          hasNewEarnings: hasNewEarnings
        }));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const statsCards = [
    {
      id: 1,
      icon: MessageSquare,
      label: 'Answered',
      value: stats.answered.toString(),
      bgColor: 'bg-primary-50',
      iconColor: 'text-navbar'
    },
    {
      id: 2,
      icon: Clock,
      label: 'Pending',
      value: stats.pending.toString(),
      bgColor: 'bg-red-50',
      iconColor: 'text-red-500',
      hasNotification: stats.pending > 0
    },
    {
      id: 3,
      icon: DollarSign,
      label: 'Earnings',
      value: `₹${stats.earnings}`,
      bgColor: 'bg-primary-50',
      iconColor: 'text-navbar',
      hasNotification: stats.hasNewEarnings
    },
    {
      id: 4,
      icon: Star,
      label: 'Avg Rating',
      value: stats.rating,
      bgColor: 'bg-yellow-50',
      iconColor: 'text-yellow-500'
    },
    // {
    //   id: 5,
    //   icon: Video,
    //   label: 'Videos',
    //   value: '0',
    //   bgColor: 'bg-orange-50',
    //   iconColor: 'text-orange-500'
    // }
  ];

  return (
    <div className="w-full min-w-0 max-w-full">
      {/* Header */}
      {/* <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-50 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-2xl sm:text-3xl">🎓</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-primary-700">Mentor Dashboard</h1>
              <p className="text-xs text-gray-600">
                Welcome back, {mentorProfile ? `${mentorProfile.firstName} ${mentorProfile.lastName}` : 'Loading...'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => navigate('/profile')}
            className="self-start sm:self-auto px-4 py-2 border border-navbar text-navbar rounded-lg text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap"
          >
            👤 Mentor Account
          </button>
        </div>
      </div> */}

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.id}
              className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 hover:shadow-md transition-shadow relative"
            >
              {/* Red dot notification badge */}
              {stat.hasNotification && (
                <div className="absolute top-2 right-2">
                  <div className="relative">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></div>
                    <div className="absolute inset-0 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping opacity-75"></div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col items-center text-center gap-1.5">
                <div className={`w-10 h-10 ${stat.bgColor} rounded-full flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.iconColor}`} />
                </div>
                <div className="text-2xl font-bold text-primary-700">
                  {stat.value}
                </div>
                <div className="text-xs text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Questions Section */}
      <Questions />
    </div>
  );
};

export default MentorDashboard;
